import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CheckCircle2, AlertTriangle, Clock, Ban, Pencil, Trash2,
  Search, Plus, Paperclip, CalendarClock, User,
} from "lucide-react";
import moment from "moment";

const STATUS_CONFIG = {
  compliant: { label: "Complert", icon: CheckCircle2, badge: "bg-emerald-100 text-emerald-700", border: "border-l-emerald-400" },
  pending: { label: "Pendent", icon: Clock, badge: "bg-amber-100 text-amber-700", border: "border-l-amber-400" },
  non_compliant: { label: "Incomplert", icon: AlertTriangle, badge: "bg-red-100 text-red-700", border: "border-l-red-400" },
  not_applicable: { label: "No aplica", icon: Ban, badge: "bg-gray-100 text-gray-500", border: "border-l-gray-200" },
};

const PRIORITY_CONFIG = {
  low: { label: "Baixa", color: "text-gray-400" },
  medium: { label: "Mitjana", color: "text-amber-500" },
  high: { label: "Alta", color: "text-orange-500" },
  critical: { label: "Crítica", color: "text-red-600" },
};

export default function ComplianceItemList({ items, onEdit, onDeleted, onAdd, isAdmin }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = items.filter(item => {
    const matchSearch = !search || item.title?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || item.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleDelete = async (id) => {
    await hosys.entities.ComplianceItem.delete(id);
    onDeleted(id);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Cercar obligacions..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Estat" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tots els estats</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && (
          <Button onClick={onAdd} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 shrink-0">
            <Plus className="w-4 h-4" /> Afegir
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-gray-200" />
          <p className="text-sm">Sense obligacions en aquesta àrea</p>
          {isAdmin && (
            <Button onClick={onAdd} variant="outline" size="sm" className="mt-3 gap-2">
              <Plus className="w-4 h-4" /> Afegir primera obligació
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
            const pc = PRIORITY_CONFIG[item.priority] || PRIORITY_CONFIG.medium;
            const StatusIcon = sc.icon;
            const isOverdue = item.due_date && moment(item.due_date).isBefore(moment(), "day") && item.status !== "compliant";
            const needsReview = item.next_review_date && moment(item.next_review_date).isSameOrBefore(moment().add(7, "days"), "day");

            return (
              <Card key={item.id} className={`p-4 border-0 shadow-sm border-l-4 ${sc.border}`}>
                <div className="flex items-start gap-3">
                  <StatusIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    item.status === "compliant" ? "text-emerald-500" :
                    item.status === "non_compliant" ? "text-red-500" :
                    item.status === "not_applicable" ? "text-gray-300" : "text-amber-500"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900 text-sm leading-tight">{item.title}</h4>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`text-[10px] font-bold uppercase ${pc.color}`}>{pc.label}</span>
                        <Badge className={`${sc.badge} border-0 text-[10px] px-2`}>{sc.label}</Badge>
                      </div>
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.description}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                      {item.responsible_name && (
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          <User className="w-3 h-3" /> {item.responsible_name}
                        </span>
                      )}
                      {item.due_date && (
                        <span className={`text-[11px] flex items-center gap-1 ${isOverdue ? "text-red-500 font-semibold" : "text-gray-400"}`}>
                          <CalendarClock className="w-3 h-3" />
                          {isOverdue ? "Vencuda " : "Límit: "}{moment(item.due_date).format("DD/MM/YYYY")}
                        </span>
                      )}
                      {needsReview && (
                        <span className="text-[11px] text-purple-600 font-medium flex items-center gap-1">
                          <CalendarClock className="w-3 h-3" /> Revisió pendent: {moment(item.next_review_date).format("DD/MM/YYYY")}
                        </span>
                      )}
                      {(item.evidence_urls || []).length > 0 && (
                        <span className="text-[11px] text-blue-500 flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> {item.evidence_urls.length} doc(s)
                        </span>
                      )}
                    </div>
                    {item.notes && (
                      <p className="text-[11px] text-gray-400 mt-1 italic line-clamp-1">{item.notes}</p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => onEdit(item)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}